import React, { useMemo } from 'react';
import { Card, Form, Row, Col, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

/**
 * Filter bar for the Report Rules list.
 * Mirrors AlertRuleFilterBar but uses the report-rule filter shape.
 */
const ReportRuleFilterBar = ({
  meters,
  filters,
  onFiltersChange,
  onClearFilters,
  sources,
  currentFinancialYear,
}) => {
  const getKpi = (m) =>
    m.financialYearKpis?.find((f) => f.financialYear === currentFinancialYear)?.kpi;

  // ── Derived options ───────────────────────────────────────────────────────

  const availableSubLocations = useMemo(() => {
    if (filters.sourceIds.length === 0) return [];
    const selectedSources = sources.filter((s) => filters.sourceIds.includes(s.value));
    const subLocs = selectedSources.flatMap((s) => s.subLocations || []);
    return Array.from(new Map(subLocs.map((sl) => [sl.value, sl])).values());
  }, [filters.sourceIds, sources]);

  const filterBase = (extra = () => true) => {
    let result = meters;
    if (filters.sourceIds.length > 0)
      result = result.filter((m) => filters.sourceIds.includes(m.sourceId));
    if (filters.subLocationIds.length > 0)
      result = result.filter((m) => filters.subLocationIds.includes(m.subLocationId));
    return result.filter(extra);
  };

  const availableModules = useMemo(() => {
    const base = filterBase();
    return [...new Set(base.map((m) => getKpi(m)?.module).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [meters, filters.sourceIds, filters.subLocationIds, currentFinancialYear]);

  const availableCategories = useMemo(() => {
    const base = filterBase((m) =>
      filters.modules.length === 0 || filters.modules.includes(getKpi(m)?.module),
    );
    return [...new Set(base.map((m) => getKpi(m)?.category).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, currentFinancialYear]);

  const availableSubCategories = useMemo(() => {
    const base = filterBase(
      (m) =>
        (filters.modules.length === 0 || filters.modules.includes(getKpi(m)?.module)) &&
        (filters.categories.length === 0 || filters.categories.includes(getKpi(m)?.category)),
    );
    return [...new Set(base.map((m) => getKpi(m)?.subCategory).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, currentFinancialYear]);

  const availableKpis = useMemo(() => {
    const base = filterBase(
      (m) =>
        (filters.modules.length === 0 || filters.modules.includes(getKpi(m)?.module)) &&
        (filters.categories.length === 0 || filters.categories.includes(getKpi(m)?.category)) &&
        (filters.subCategories.length === 0 ||
          filters.subCategories.includes(getKpi(m)?.subCategory)),
    );
    return [...new Set(base.map((m) => getKpi(m)?.kpi).filter(Boolean))].map((v) => ({
      value: v,
      label: v,
    }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.subCategories, currentFinancialYear]);

  const availableMeterIds = useMemo(() => {
    const base = filterBase(
      (m) =>
        (filters.modules.length === 0 || filters.modules.includes(getKpi(m)?.module)) &&
        (filters.categories.length === 0 || filters.categories.includes(getKpi(m)?.category)) &&
        (filters.subCategories.length === 0 ||
          filters.subCategories.includes(getKpi(m)?.subCategory)) &&
        (filters.kpis.length === 0 || filters.kpis.includes(getKpi(m)?.kpi)),
    );
    const map = new Map();
    base.forEach((m) => {
      if (m.meterId && !map.has(m.meterId)) map.set(m.meterId, m.meterName || m.meterId);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [meters, filters.sourceIds, filters.subLocationIds, filters.modules, filters.categories, filters.subCategories, filters.kpis, currentFinancialYear]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const handleFilterChange = (field, value) => {
    const next = { ...filters, [field]: value };
    if (field === 'sourceIds' && value.length === 0) next.subLocationIds = [];
    onFiltersChange(next);
  };

  const hasActiveFilters =
    filters.sourceIds.length > 0 ||
    filters.subLocationIds.length > 0 ||
    filters.modules.length > 0 ||
    filters.categories.length > 0 ||
    filters.subCategories.length > 0 ||
    filters.kpis.length > 0 ||
    filters.meterIds.length > 0 ||
    filters.enabledStatus !== null;

  const sharedSelectProps = {
    className: 'react-select-container',
    classNamePrefix: 'react-select',
    isClearable: true,
    closeMenuOnSelect: false,
    menuPortalTarget: document.body,
    menuPosition: 'fixed',
    styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Card className="filter-card mb-4">
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">
            <FontAwesomeIcon icon={faFilter} className="me-2" />
            Filters
          </h6>
          {hasActiveFilters && (
            <Button variant="outline-secondary" size="sm" onClick={onClearFilters}>
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Clear All
            </Button>
          )}
        </div>

        <Row>
          <Col md={4} className="mb-3">
            <Form.Label>Location</Form.Label>
            <Select
              isMulti
              options={sources}
              value={sources.filter((s) => filters.sourceIds.includes(s.value))}
              onChange={(sel) => handleFilterChange('sourceIds', sel.map((s) => s.value))}
              placeholder="All locations..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Sub Location</Form.Label>
            <Select
              isMulti
              options={availableSubLocations}
              value={availableSubLocations.filter((sl) => filters.subLocationIds.includes(sl.value))}
              onChange={(sel) => handleFilterChange('subLocationIds', sel.map((sl) => sl.value))}
              placeholder="All sub locations..."
              isDisabled={availableSubLocations.length === 0}
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Module</Form.Label>
            <Select
              isMulti
              options={availableModules}
              value={availableModules.filter((m) => filters.modules.includes(m.value))}
              onChange={(sel) => handleFilterChange('modules', sel.map((m) => m.value))}
              placeholder="All modules..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Category</Form.Label>
            <Select
              isMulti
              options={availableCategories}
              value={availableCategories.filter((c) => filters.categories.includes(c.value))}
              onChange={(sel) => handleFilterChange('categories', sel.map((c) => c.value))}
              placeholder="All categories..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Sub Category</Form.Label>
            <Select
              isMulti
              options={availableSubCategories}
              value={availableSubCategories.filter((sc) => filters.subCategories.includes(sc.value))}
              onChange={(sel) => handleFilterChange('subCategories', sel.map((sc) => sc.value))}
              placeholder="All sub categories..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>KPI</Form.Label>
            <Select
              isMulti
              options={availableKpis}
              value={availableKpis.filter((k) => filters.kpis.includes(k.value))}
              onChange={(sel) => handleFilterChange('kpis', sel.map((k) => k.value))}
              placeholder="All KPIs..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Meter ID</Form.Label>
            <Select
              isMulti
              options={availableMeterIds}
              value={availableMeterIds.filter((m) => filters.meterIds.includes(m.value))}
              onChange={(sel) => handleFilterChange('meterIds', sel.map((m) => m.value))}
              placeholder="All meters..."
              {...sharedSelectProps}
            />
          </Col>

          <Col md={4} className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.enabledStatus === null ? 'all' : String(filters.enabledStatus)}
              onChange={(e) => {
                const val = e.target.value === 'all' ? null : e.target.value === 'true';
                handleFilterChange('enabledStatus', val);
              }}
            >
              <option value="all">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </Form.Select>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

export default ReportRuleFilterBar;